import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserContext } from '../contexts/UserContext';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import ApartmentIcon from '@mui/icons-material/Apartment';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';
import InfoIcon from '@mui/icons-material/Info';
import LoginIcon from '@mui/icons-material/Login';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

import { Main, AppBar, DrawerHeader, Footer } from '../components/LayoutComponents';
import FooterContent from '../components/FooterContent';
import MenuButton from './MenuButton';
import { appbarMenuButtonItems } from './appbarMenuButtonItems';

const drawerWidth = 240;

export default function Layout({ children }) {
    const { token, username, logout } = useUserContext();
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    const handleDrawerOpen = () => setOpen(true);
    const handleDrawerClose = () => setOpen(false);
    const handleSignIn = () => navigate('/signin');
    const handleSignOut = () => {
        logout();
        navigate('/signin');
    };

    const navItems = token
        ? [
            { title: 'Homepage', icon: <HomeIcon sx={{ color: theme.palette.primary.main }} />, path: '/' },
            { title: 'Buildings', icon: <ApartmentIcon sx={{ color: theme.palette.primary.main }} />, path: '/buildings' },
            {
                title: 'Decarbonization Actions',
                icon: <EnergySavingsLeafIcon sx={{ color: theme.palette.primary.main }} />,
                path: '/decarbonization-actions'
            },
            { title: 'About', icon: <InfoIcon sx={{ color: theme.palette.primary.main }} />, path: '/about' },
            { title: 'Sign Out', icon: <ExitToAppIcon sx={{ color: theme.palette.primary.main }} />, action: handleSignOut },
        ]
        : [
            { title: 'Homepage', icon: <HomeIcon sx={{ color: theme.palette.primary.main }} />, path: '/' },
            { title: 'About', icon: <InfoIcon sx={{ color: theme.palette.primary.main }} />, path: '/about' },
            { title: 'Sign In', icon: <LoginIcon sx={{ color: theme.palette.primary.main }} />, action: handleSignIn },
        ];

    return (
        <>
            <Box sx={{ display: 'flex', minHeight: `calc(100vh - 60px)` }}>
                <CssBaseline />
                <AppBar position="fixed" open={open}>
                    <Toolbar>
                        <Grid container alignItems="center">
                            <Grid item>
                                <Stack direction={'row'} alignItems="center">
                                    <IconButton
                                        color="inherit"
                                        aria-label="open drawer"
                                        onClick={handleDrawerOpen}
                                        edge="start"
                                        sx={{ mr: 2, color: 'white', ...(open && { display: 'none' }) }}
                                    >
                                        <MenuIcon />
                                    </IconButton>
                                    <Typography sx={{ display: { xs: "none", lg: "block" } }} color={'white'}>META BUILD
                                        Decarbonization Tool</Typography>
                                </Stack>
                            </Grid>

                            {token && (
                                <>
                                    <Typography
                                        sx={{ ml: 'auto' }}
                                        style={{ color: 'white' }}
                                    >
                                        Welcome, {username}
                                    </Typography>
                                    <MenuButton subLinks={appbarMenuButtonItems} signout={handleSignOut} />
                                </>
                            )}
                        </Grid>
                    </Toolbar>
                </AppBar>

                <Drawer
                    sx={{
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                        },
                    }}
                    variant="persistent"
                    anchor="left"
                    open={open}
                >
                    <DrawerHeader>
                        <img src="/images/metabuild_logo.svg" alt="" height={'60px'} style={{ objectFit: 'cover' }} />
                        <IconButton onClick={handleDrawerClose}>
                            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    </DrawerHeader>
                    <Divider />
                    <List>
                        {navItems.map(menuItem => (
                            <ListItem
                                key={menuItem.title}
                                disablePadding
                                sx={{
                                    background: location.pathname === menuItem.path ? theme.palette.gradient.main : '',
                                    border: location.pathname === menuItem.path ? '1px solid #31bebe' : '',
                                    borderRadius: '10px',
                                    margin: 1,
                                    width: '95%'
                                }}
                            >
                                <ListItemButton
                                    onClick={() => menuItem.action ? menuItem.action() : navigate(menuItem.path)}>
                                    <ListItemIcon>
                                        {React.cloneElement(menuItem.icon, {
                                            style: { color: location.pathname === menuItem.path ? 'white' : '' }
                                        })}
                                    </ListItemIcon>

                                    <ListItemText
                                        primary={
                                            <Typography
                                                fontWeight={500}
                                                fontSize={17}
                                                align={'left'}
                                                color={location.pathname === menuItem.path ? 'white' : 'normal'}
                                            >
                                                {menuItem.title}
                                            </Typography>
                                        }
                                    />
                                </ListItemButton>

                            </ListItem>
                        ))}
                    </List>
                    <Divider />
                </Drawer>

                <Main open={open} sx={{ background: '#fefefe' }}>
                    <DrawerHeader />
                    {children}
                </Main>
            </Box>

            <Footer open={open} sx={{ position: 'sticky', mt: 'auto' }}>
                <FooterContent />
            </Footer>
        </>
    );
}
